import type { DashboardCampaign } from "@adpilot/shared";
import { IconChevronRight } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { CampaignBiddingStrategyCell } from "@/components/campaign/CampaignBiddingStrategyCell";
import { CampaignShopCell } from "@/components/campaign/CampaignShopCell";
import { CampaignStartDateCell } from "@/components/campaign/CampaignStartDateCell";
import { CampaignStateMenu } from "@/components/campaign/CampaignStateMenu";
import { CampaignStatusBadge } from "@/components/campaign/CampaignStatusBadge";
import { CampaignTargetingTypeCell } from "@/components/campaign/CampaignTargetingTypeCell";
import { CampaignTypeBadge } from "@/components/campaign/CampaignTypeBadge";
import {
  CAMPAIGN_NAME_STICKY_POSITION,
  CAMPAIGN_SWITCH_STICKY_POSITION,
  CAMPAIGN_TABLE_CELL_INSET,
  CAMPAIGN_TABLE_GRID,
  type CampaignTableColumnId,
} from "@/components/campaign/campaign-table-layout";
import { DataGridRow } from "@/components/patterns/DataTable";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { policyLabel, runGroup, runLabel } from "./campaign-list-filters";

export function CampaignRow({
  campaign,
  canWrite,
  currencyCode = "USD",
  gridStyle,
  switchWidth,
  visibleColumnIds,
}: {
  campaign: DashboardCampaign;
  canWrite: boolean;
  /** The scope's reporting currency for the (possibly converted) metric sums. */
  currencyCode?: string;
  gridStyle: CSSProperties;
  switchWidth: number;
  visibleColumnIds: readonly CampaignTableColumnId[];
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency: formatCurrencyBase, formatNumber, formatPercent } = useFormatters();
  const formatCurrency = (value: number) => formatCurrencyBase(value, currencyCode);
  const cell = (columnId: CampaignTableColumnId, children: React.ReactNode, numeric = false) => (
    <div
      key={columnId}
      className={cn(
        "flex min-w-0 items-center",
        CAMPAIGN_TABLE_CELL_INSET,
        numeric && "justify-end text-right",
      )}
    >
      {children}
    </div>
  );
  const renderColumn = (columnId: CampaignTableColumnId) => {
    switch (columnId) {
      case "switch":
        return (
          <div
            key={columnId}
            className={cn(
              CAMPAIGN_SWITCH_STICKY_POSITION,
              "z-20 flex self-stretch items-center border-r border-line bg-inherit pl-3.5",
            )}
          >
            <CampaignStateMenu campaign={campaign} canWrite={canWrite} />
          </div>
        );
      case "campaign":
        return (
          <Link
            key={columnId}
            to="/campaigns/$id"
            params={{ id: campaign.id }}
            className={cn(
              CAMPAIGN_NAME_STICKY_POSITION,
              "z-20 flex min-w-0 items-center self-stretch border-r border-line bg-inherit pl-3.5 pr-4 text-ink shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]",
            )}
            style={{ left: switchWidth }}
          >
            <span className="flex min-w-0 items-center gap-2">
              <CampaignTypeBadge campaignType={campaign.campaignType} />
              <span className="truncate text-[11px] font-semibold leading-4">{campaign.name}</span>
            </span>
          </Link>
        );
      case "delivery":
        return cell(columnId, <CampaignStatusBadge status={campaign.status} />);
      case "shop":
        return cell(columnId, <CampaignShopCell shopName={campaign.shopName} />);
      case "managedGroup":
        return cell(
          columnId,
          campaign.managedGroup ? (
            <Link
              to="/managed-groups/$id"
              params={{ id: campaign.managedGroup.id }}
              className="truncate text-[11px] font-medium text-brand hover:underline"
            >
              {campaign.managedGroup.name}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        );
      case "aiPolicy":
        return cell(
          columnId,
          <span
            className={cn(
              "rounded-full border border-line bg-subtle px-2 py-1 text-[9px] leading-none text-muted-foreground",
              campaign.aiPolicyState === "override" && "border-amber/20 bg-amber/5 text-amber-11",
            )}
          >
            {policyLabel(t, campaign.aiPolicyState)}
          </span>,
        );
      case "targetingType":
        return cell(columnId, <CampaignTargetingTypeCell targetingType={campaign.targetingType} />);
      case "biddingStrategy":
        return cell(
          columnId,
          <CampaignBiddingStrategyCell strategy={campaign.bidOptimizationStrategy} />,
        );
      case "startDate":
        return cell(columnId, <CampaignStartDateCell startDate={campaign.startDate} />);
      case "dailyBudget":
        return cell(
          columnId,
          <Metric
            value={campaign.dailyBudget == null ? "--" : formatCurrency(campaign.dailyBudget)}
          />,
          true,
        );
      case "currentBudget":
        return cell(columnId, <Metric value="--" />, true);
      case "clickCost":
        return cell(
          columnId,
          <Metric
            value={campaign.metrics.clicks > 0 ? formatCurrency(campaign.metrics.cpc) : "--"}
          />,
          true,
        );
      case "orderCost":
        return cell(
          columnId,
          <Metric
            value={
              campaign.metrics.orders > 0
                ? formatCurrency(campaign.metrics.spend / campaign.metrics.orders)
                : "--"
            }
          />,
          true,
        );
      case "ctr":
        return cell(columnId, <Metric value={formatPercent(campaign.metrics.ctr, 2)} />, true);
      case "cvr":
        return cell(columnId, <Metric value={formatPercent(campaign.metrics.cvr, 2)} />, true);
      case "acos":
        return cell(columnId, <Metric value={formatPercent(campaign.metrics.acos)} />, true);
      case "roas":
        return cell(
          columnId,
          <Metric
            value={formatNumber(campaign.metrics.roas, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />,
          true,
        );
      case "spend":
        return cell(columnId, <Metric value={formatCurrency(campaign.metrics.spend)} />, true);
      case "impressions":
        return cell(
          columnId,
          <Metric
            value={formatNumber(campaign.metrics.impressions, { maximumFractionDigits: 0 })}
          />,
          true,
        );
      case "clicks":
        return cell(
          columnId,
          <Metric value={formatNumber(campaign.metrics.clicks, { maximumFractionDigits: 0 })} />,
          true,
        );
      case "orders":
        return cell(
          columnId,
          <Metric value={formatNumber(campaign.metrics.orders, { maximumFractionDigits: 0 })} />,
          true,
        );
      case "units":
      case "advertisedProductOrders":
        return cell(columnId, <Metric value="--" />, true);
      case "sales":
        return cell(columnId, <Metric value={formatCurrency(campaign.metrics.sales)} />, true);
      case "latestRun":
        return cell(
          columnId,
          <span
            className={cn(
              "flex items-center justify-end gap-1.5 text-right",
              runGroup(campaign) === "attention" && "text-amber-11",
              runGroup(campaign) === "completed" && "text-green-11",
            )}
          >
            <span className="size-1.5 rounded-full bg-current opacity-80" />
            {runLabel(t, campaign)}
          </span>,
          true,
        );
    }
  };
  return (
    <DataGridRow
      density="standard"
      className={cn(CAMPAIGN_TABLE_GRID, "bg-surface")}
      style={gridStyle}
    >
      {visibleColumnIds.map(renderColumn)}
      <Link
        aria-label={t("list.openAria", { name: campaign.name })}
        to="/campaigns/$id"
        params={{ id: campaign.id }}
        className="ml-auto text-muted-foreground"
      >
        <IconChevronRight className="size-3.5" />
      </Link>
    </DataGridRow>
  );
}

function Metric({ value }: { value: string }) {
  return <span className="text-right font-mono tabular-nums text-ink">{value}</span>;
}
